import React, {
  useState,
  useRef,
  useMemo,
  useCallback,
  useLayoutEffect,
  useEffect,
} from "react";
import { useSelector } from "react-redux";
import { Card } from "../UI";
import { font, FUNNEL_STAGES, LLM_PLATFORMS, LANGUAGES, DEFAULT_LLM_PLATFORM_IDS } from "./constants";
import SectionDivider from "./SectionDivider";
import FormInput from "./FormInput";
import TagInput from "./TagInput";
import SearchableDrp from "../common/SearchableDrp";
import ParsedAudiencesPanel from "./ParsedAudiencesPanel";
import {
  SKIP_AUDIENCE_SELECTION_STEP,
  getFirstAudienceId,
  resolveSelectedAudienceId,
  buildGenerateQueriesPayload,
} from "./audienceSelection";
import AudienceContinueButton from "./AudienceContinueButton";
import GeneratePersonaConfirmModal from "../Modals/GeneratePersonaConfirmModal";
import {
  generatePersonaFromAi,
  getPersonaFromLibrary,
  parseJsonFile,
  reRunSimulation,
  saveSimulation,
} from "../../requests";
import { notifyError, notifySuccess } from "../../utils/helper";
import { useDispatch } from "react-redux";
import { toggleRefresh } from "../../_store/reducers/refreshReducer";

const LLM_ID_TO_API_PLATFORM = {
  chatgpt: "chatgpt",
  claude: "claude",
  gemini: "gemini",
  perplexity: "perplexity",
};

const GENERATING_AUDIENCE_OVERLAY_MESSAGE =
  "Generating your audience… Please remain on this page and do not leave or refresh—it may take a couple of minutes.";
const FRONTEND_BASE_URL =
  process.env.REACT_APP_FRONTEND_URL || window.location.origin;

/**
 * @typedef {Object} DuplicateSimulationData
 * @property {number|string} [simulationId]
 * @property {string} [simulationName]
 * @property {string} [simulationDescription]
 * @property {string[]} [audienceList]
 * @property {string[]} [competitorBrands]
 * @property {string} [funnelStage] funnel id or label (e.g. awareness / Awareness)
 * @property {string} [funnelSatge] typo alias from API
 * @property {string[]} [llmPlatforms] ids or labels (e.g. chatgpt, ChatGPT)
 * @property {string} [language]
 * @property {Record<string, unknown>} [personaJson] full GWI-style persona object
 * @property {string} [personaFilename]
 * @property {string|null} [selectedAudience] e.g. audience_1 — preselect row in library audiences table
 */

function normalizeLanguage(v) {
  if (v == null || v === "") return "English";
  const s = String(v);
  const found = LANGUAGES.find((l) => l.toLowerCase() === s.toLowerCase());
  return found ?? s;
}

function normalizeFunnelId(v) {
  if (v == null || v === "") return "awareness";
  const raw = String(v).trim();
  const asId = raw.toLowerCase().replace(/\s+/g, "_");
  const byId = FUNNEL_STAGES.find((st) => st.id === asId);
  if (byId) return byId.id;
  const byLabel = FUNNEL_STAGES.find(
    (st) => st.label.toLowerCase() === raw.toLowerCase(),
  );
  return byLabel?.id ?? "awareness";
}

function normalizeLlmIds(arr) {
  if (!Array.isArray(arr) || arr.length === 0) return [...DEFAULT_LLM_PLATFORM_IDS];
  const ids = arr
    .map((x) => {
      const s = String(x).trim().toLowerCase();
      if (s === "chat-gpt") return "chatgpt";
      const byId = LLM_PLATFORMS.find((p) => p.id === s);
      if (byId) return byId.id;
      const byLabel = LLM_PLATFORMS.find((p) => p.label.toLowerCase() === s);
      return byLabel?.id;
    })
    .filter(Boolean);
  return ids.length ? ids : [...DEFAULT_LLM_PLATFORM_IDS];
}

function normalizeCompetitorList(v) {
  if (!Array.isArray(v)) return [];
  return v.map((x) => String(x).trim()).filter(Boolean);
}

function prefillSnapshotKey(data) {
  const d = data && typeof data === "object" ? data : {};
  try {
    return JSON.stringify({
      id: d.simulationId ?? d.simulation_id,
      name: d.simulationName ?? d.simulation_name,
      desc: d.simulationDescription ?? d.simulation_description,
      language: d.language,
      funnel: d.funnelStage ?? d.funnelSatge,
      llm: d.llmPlatforms ?? d.llm_platforms,
      competitors: d.competitorBrands ?? d.competitor_brands ?? d.competitors,
      personaJson: d.personaJson ?? d.persona_json,
      audiences: d.audienceList ?? d.audience_list,
      selectedAudience: d.selectedAudience ?? d.selected_audience,
      audiencePrompt: d.audiencePrompt ?? d.audience_prompt ?? d.gwiPrompt,
    });
  } catch {
    return String(d?.simulationId ?? d?.simulation_id ?? "");
  }
}

function buildPersonaFromData(data) {
  const d = data && typeof data === "object" ? data : {};
  const pj = d.personaJson ?? d.persona_json;
  if (pj && typeof pj === "object" && !Array.isArray(pj)) {
    const text = JSON.stringify(pj, null, 2);
    const fname = d.personaFilename ?? d.persona_filename ?? "persona.json";
    const blob = new Blob([text], { type: "application/json" });
    const file = new File([blob], fname, { type: "application/json" });
    return { file, fileName: fname };
  }
  const list = d.audienceList ?? d.audience_list;
  if (Array.isArray(list) && list.length > 0) {
    const jsonObj = {
      client: d.client ?? "",
      market: d.market ?? "",
      category: d.category ?? "",
      audience: list.filter(Boolean).join("; "),
      ageRange: d.ageRange ?? d.age_range ?? "",
      timePeriods: d.timePeriods ?? d.time_periods ?? "",
    };
    const text = JSON.stringify(jsonObj, null, 2);
    const fname = "audience-from-copy.json";
    const blob = new Blob([text], { type: "application/json" });
    const file = new File([blob], fname, { type: "application/json" });
    return { file, fileName: fname };
  }
  return null;
}

function getPrefillAudiencePrompt(data) {
  const d = data && typeof data === "object" ? data : {};
  return String(
    d.audiencePrompt ?? d.audience_prompt ?? d.gwiPrompt ?? d.gwi_description ?? "",
  ).trim();
}

function formDefaultsFromData(data) {
  const d = data && typeof data === "object" ? data : {};
  const baseName = d.simulationName ?? d.simulation_name ?? d.name ?? "";
  return {
    projectName: baseName
      ? `${String(baseName)
          .replace(/\s*\(copy\)\s*$/i, "")
          .trim()} (Copy)`
      : "",
    simulationDescription:
      d.simulationDescription ?? d.simulation_description ?? "",
    language: normalizeLanguage(d.language),
    funnelStage: normalizeFunnelId(d.funnelStage ?? d.funnelSatge),
    llmPlatforms: normalizeLlmIds(d.llmPlatforms ?? d.llm_platforms),
  };
}

function emptyUploadSnapshot() {
  return {
    personaFile: null,
    personaFileName: "",
    parsedAudiences: null,
    parsedSimulationId: null,
    selectedParsedAudienceId: null,
    fieldsDisabled: false,
  };
}

function emptyLibrarySnapshot() {
  return {
    selectedAudienceId: null,
    personaFileName: "",
    parsedAudiences: null,
    parsedSimulationId: null,
    selectedParsedAudienceId: null,
  };
}

function emptyGenerateSnapshot() {
  return {
    gwiPrompt: "",
    gwiGenerated: false,
    personaFileName: "",
    parsedAudiences: null,
    parsedSimulationId: null,
    selectedParsedAudienceId: null,
    fieldsDisabled: false,
  };
}

function DuplicateSimulation({
  data = {},
  onBack,
  onSaveAndGo = () => {},
  onQueriesGenerationStarted = () => {},
}) {
  const dispatch = useDispatch();
  const context = useSelector((state) => state.context);
  const [form, setForm] = useState(() => formDefaultsFromData(data));
  const [competitors, setCompetitors] = useState(() =>
    normalizeCompetitorList(
      data?.competitorBrands ?? data?.competitor_brands ?? data?.competitors,
    ),
  );
  const [personaFile, setPersonaFile] = useState(null);
  const [personaFileName, setPersonaFileName] = useState("");
  const [audienceMode, setAudienceMode] = useState(() =>
    getPrefillAudiencePrompt(data) ? "generate" : "upload",
  );
  const [gwiPrompt, setGwiPrompt] = useState(() => getPrefillAudiencePrompt(data));
  const [gwiGenerating, setGwiGenerating] = useState(false);
  const [gwiGenerated, setGwiGenerated] = useState(false);
  const [generateConfirmOpen, setGenerateConfirmOpen] = useState(false);
  const [generatePopupMessage, setGeneratePopupMessage] = useState(
    "Persona generation has started. It may take a few minutes. Click OK to go to the Simulations page.",
  );
  const [selectedAudienceId, setSelectedAudienceId] = useState(null);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [parsedAudiences, setParsedAudiences] = useState(null);
  const [parsedSimulationId, setParsedSimulationId] = useState(null);
  const [selectedParsedAudienceId, setSelectedParsedAudienceId] =
    useState(null);
  const [personaParseLoading, setPersonaParseLoading] = useState(false);
  const [personaLibraryLoading, setPersonaLibraryLoading] = useState(false);
  const [fieldsDisabled, setFieldsDisabled] = useState(false);
  const [availableAudiences, setAvailableAudiences] = useState([]);
  const personaInputRef = useRef(null);
  /** Set after we apply library row + audiences for current `data` snapshot (see useEffect). */
  const libraryPreselectAppliedForKey = useRef(null);
  /** Persisted when leaving Upload New so tab switches restore file + parse results. */
  const uploadSnapshotRef = useRef(emptyUploadSnapshot());
  /** Persisted when leaving Select from Library so tab switches restore library selection. */
  const librarySnapshotRef = useRef(emptyLibrarySnapshot());
  /** Persisted when leaving Generate so tab switches restore GWI demo state. */
  const generateSnapshotRef = useRef(emptyGenerateSnapshot());

  const selectedBrandName = context?.brand_name ?? "";

  const selectedAudience = useMemo(
    () => availableAudiences.find((a) => a.id === selectedAudienceId),
    [availableAudiences, selectedAudienceId],
  );

  const canSavePersona = useMemo(
    () =>
      Boolean(
        parsedSimulationId != null &&
        String(parsedSimulationId).trim() !== "" &&
        resolveSelectedAudienceId(parsedAudiences, selectedParsedAudienceId),
      ),
    [parsedSimulationId, selectedParsedAudienceId, parsedAudiences],
  );

  const resetGenerateUi = useCallback(() => {
    setGwiPrompt("");
    setGwiGenerated(false);
    setGwiGenerating(false);
    setGenerateConfirmOpen(false);
  }, []);

  const applyPrefill = useCallback(
    (nextData) => {
      libraryPreselectAppliedForKey.current = null;
      uploadSnapshotRef.current = emptyUploadSnapshot();
      librarySnapshotRef.current = emptyLibrarySnapshot();
      generateSnapshotRef.current = emptyGenerateSnapshot();
      resetGenerateUi();
      setFieldsDisabled(false);
      setParsedAudiences(null);
      setParsedSimulationId(null);
      setSelectedParsedAudienceId(null);
      setForm(formDefaultsFromData(nextData));
      setCompetitors(
        normalizeCompetitorList(
          nextData?.competitorBrands ??
            nextData?.competitor_brands ??
            nextData?.competitors,
        ),
      );
      const prompt = getPrefillAudiencePrompt(nextData);
      const built = buildPersonaFromData(nextData);
      if (prompt) {
        setPersonaFile(null);
        setPersonaFileName("");
        setSelectedAudienceId(null);
        setAudienceMode("generate");
        setGwiPrompt(prompt);
        if (personaInputRef.current) personaInputRef.current.value = "";
      } else if (built) {
        setPersonaFile(built.file);
        setPersonaFileName(built.fileName);
        setSelectedAudienceId(null);
        setAudienceMode("upload");
        setGwiPrompt("");
        if (personaInputRef.current) personaInputRef.current.value = "";
      } else {
        setPersonaFile(null);
        setPersonaFileName("");
        setSelectedAudienceId(null);
        setAudienceMode("upload");
        setGwiPrompt("");
        if (personaInputRef.current) personaInputRef.current.value = "";
      }
    },
    [resetGenerateUi],
  );

  const lastPrefillKey = useRef("");
  useLayoutEffect(() => {
    const key = prefillSnapshotKey(data);
    if (key === lastPrefillKey.current) return;
    lastPrefillKey.current = key;
    applyPrefill(data && typeof data === "object" ? data : {});
  }, [data, applyPrefill]);

  const clearPersona = useCallback(() => {
    setPersonaParseLoading(false);
    if (personaInputRef.current) personaInputRef.current.value = "";
    if (audienceMode === "upload") {
      setPersonaFile(null);
      setPersonaFileName("");
      setParsedAudiences(null);
      setParsedSimulationId(null);
      setSelectedParsedAudienceId(null);
      setFieldsDisabled(false);
      uploadSnapshotRef.current = emptyUploadSnapshot();
    } else if (audienceMode === "generate") {
      resetGenerateUi();
      setPersonaFileName("");
      setParsedAudiences(null);
      setParsedSimulationId(null);
      setSelectedParsedAudienceId(null);
      setFieldsDisabled(false);
      generateSnapshotRef.current = emptyGenerateSnapshot();
    } else {
      setSelectedAudienceId(null);
      setPersonaFile(null);
      setPersonaFileName("");
      setParsedAudiences(null);
      setParsedSimulationId(null);
      setSelectedParsedAudienceId(null);
      librarySnapshotRef.current = emptyLibrarySnapshot();
    }
  }, [audienceMode, resetGenerateUi]);

  const handleAudienceMode = (mode) => {
    if (mode === audienceMode) return;

    if (audienceMode === "upload") {
      uploadSnapshotRef.current = {
        personaFile,
        personaFileName,
        parsedAudiences,
        parsedSimulationId,
        selectedParsedAudienceId,
        fieldsDisabled,
      };
    } else if (audienceMode === "library") {
      librarySnapshotRef.current = {
        selectedAudienceId,
        personaFileName,
        parsedAudiences,
        parsedSimulationId,
        selectedParsedAudienceId,
      };
    } else if (audienceMode === "generate") {
      generateSnapshotRef.current = {
        gwiPrompt,
        gwiGenerated,
        personaFileName,
        parsedAudiences,
        parsedSimulationId,
        selectedParsedAudienceId,
        fieldsDisabled,
      };
    }

    if (mode === "library") {
      const lib = librarySnapshotRef.current;
      const fromCompletedGwi = audienceMode === "generate" && gwiGenerated;
      setAudienceMode("library");
      setSelectedAudienceId(lib.selectedAudienceId);
      setPersonaFile(null);
      if (fromCompletedGwi) {
        const gen = generateSnapshotRef.current;
        setFieldsDisabled(!!gen.fieldsDisabled);
        setPersonaFileName(gen.personaFileName ?? "");
        setParsedAudiences(gen.parsedAudiences);
        setParsedSimulationId(gen.parsedSimulationId);
        setSelectedParsedAudienceId(gen.selectedParsedAudienceId);
      } else {
        setFieldsDisabled(false);
        setPersonaFileName(lib.personaFileName ?? "");
        setParsedAudiences(lib.parsedAudiences);
        setParsedSimulationId(lib.parsedSimulationId);
        setSelectedParsedAudienceId(lib.selectedParsedAudienceId);
      }
      setGwiPrompt("");
      setGwiGenerated(false);
      setGwiGenerating(false);
      setGenerateConfirmOpen(false);
      void fetchPersonaFromLibrary();
    } else if (mode === "upload") {
      const up = uploadSnapshotRef.current;
      const fromCompletedGwi = audienceMode === "generate" && gwiGenerated;
      setAudienceMode("upload");
      setSelectedAudienceId(null);
      if (fromCompletedGwi) {
        const gen = generateSnapshotRef.current;
        setPersonaFile(null);
        setPersonaFileName(gen.personaFileName ?? "");
        setParsedAudiences(gen.parsedAudiences);
        setParsedSimulationId(gen.parsedSimulationId);
        setSelectedParsedAudienceId(gen.selectedParsedAudienceId);
        setFieldsDisabled(!!gen.fieldsDisabled);
      } else {
        setPersonaFile(up.personaFile);
        setPersonaFileName(up.personaFileName ?? "");
        setParsedAudiences(up.parsedAudiences);
        setParsedSimulationId(up.parsedSimulationId);
        setSelectedParsedAudienceId(up.selectedParsedAudienceId);
        setFieldsDisabled(!!up.fieldsDisabled);
      }
      setGwiPrompt("");
      setGwiGenerated(false);
      setGwiGenerating(false);
      setGenerateConfirmOpen(false);
      if (personaInputRef.current) personaInputRef.current.value = "";
    } else if (mode === "generate") {
      const gen = generateSnapshotRef.current;
      setAudienceMode("generate");
      setGwiPrompt(gen.gwiPrompt ?? "");
      setGwiGenerated(!!gen.gwiGenerated);
      setGwiGenerating(false);
      setGenerateConfirmOpen(false);
      setSelectedAudienceId(null);
      setPersonaFile(null);
      setPersonaFileName(gen.personaFileName ?? "");
      setParsedAudiences(gen.parsedAudiences);
      setParsedSimulationId(gen.parsedSimulationId);
      setSelectedParsedAudienceId(gen.selectedParsedAudienceId);
      setFieldsDisabled(!!gen.fieldsDisabled && !!gen.gwiGenerated);
      if (personaInputRef.current) personaInputRef.current.value = "";
    }
  };

  const handleConfirmGeneratePersona = useCallback(async () => {
    if (!validate()) return;
    if (context?.project_id == null || context?.project_id === "") {
      notifyError("Select a project in session context before generating.");
      return;
    }
    const stage = FUNNEL_STAGES.find((s) => s.id === form.funnelStage);
    const llm_platforms = Array.isArray(form.llmPlatforms)
      ? form.llmPlatforms.map((id) => LLM_ID_TO_API_PLATFORM[id] ?? id)
      : [];
    const llm_platform = llm_platforms[0] ?? null;
    if (!llm_platform) {
      notifyError("Select an LLM platform");
      return;
    }

    const promptText = gwiPrompt.trim();
    setGenerateConfirmOpen(false);
    setGwiGenerating(true);
    try {
      const body = {
        gwi_description: promptText,
        funnel_stage: stage?.label ?? form.funnelStage,
        llm_platform,
        llm_platforms,
        language: String(form.language ?? "English").toLowerCase(),
        competitor_brands: competitors,
        project_id: Number(context.project_id),
        name: form.projectName?.trim() || "Auto generate json",
      };
      const simDesc = form.simulationDescription?.trim();
      if (simDesc) body.simulation_description = simDesc;

      const res = await generatePersonaFromAi(body);
      if (res?.status >= 400) {
        notifyError(res?.data?.message ?? "Failed to generate persona");
        return;
      }

      const payload = res?.data ?? {};
      const respInner =
        payload.response && typeof payload.response === "object"
          ? payload.response
          : payload;
      const list = Array.isArray(respInner.audiences)
        ? respInner.audiences
        : Array.isArray(respInner.audience_list)
          ? respInner.audience_list
          : [];
      const simulationId =
        payload.simulation_id ?? respInner.simulation_id ?? null;
      if (list.length > 0) {
        const fname =
          respInner.persona_name ??
          (form.projectName?.trim()
            ? `${form.projectName.trim().replace(/\s+/g, "_")}.json`
            : respInner.brand_name
              ? `${String(respInner.brand_name).replace(/\s+/g, "_")}_persona.json`
              : "generated_persona.json");
        const pid = getFirstAudienceId(list);

        setParsedSimulationId(simulationId);
        setParsedAudiences(list);
        setSelectedParsedAudienceId(pid);
        setPersonaFile(null);
        setPersonaFileName(fname);
        setGwiGenerated(true);
        setFieldsDisabled(true);
        generateSnapshotRef.current = {
          gwiPrompt: promptText,
          gwiGenerated: true,
          personaFileName: fname,
          parsedAudiences: list,
          parsedSimulationId: simulationId,
          selectedParsedAudienceId: pid,
          fieldsDisabled: true,
        };
        notifySuccess(
          "Persona generated successfully. Click Continue to generate queries.",
        );
      } else {
        setGeneratePopupMessage(
          "Persona generation has started. It may take a few minutes. Click OK to go to the Simulations page.",
        );
        setGenerateConfirmOpen(true);
      }
    } catch (e) {
      notifyError(e?.response?.data?.message ?? "Failed to generate persona");
      setFieldsDisabled(false);
    } finally {
      setGwiGenerating(false);
    }
  }, [gwiPrompt, form, competitors, context]);

  const clearGeneratePersona = useCallback(() => {
    resetGenerateUi();
    setFieldsDisabled(false);
    setPersonaFileName("");
    setParsedAudiences(null);
    setParsedSimulationId(null);
    setSelectedParsedAudienceId(null);
    generateSnapshotRef.current = emptyGenerateSnapshot();
  }, [resetGenerateUi]);

  const handleGeneratePersonaRedirect = useCallback(() => {
    setGenerateConfirmOpen(false);
    const params = new URLSearchParams();
    if (context?.project_id != null && context.project_id !== "") {
      params.set("project_id", String(context.project_id));
    }
    if (context?.brand_id != null && context.brand_id !== "") {
      params.set("brand_id", String(context.brand_id));
    }
    if (context?.agency_id != null && context.agency_id !== "") {
      params.set("agency_id", String(context.agency_id));
    }
    const query = params.toString();
    window.location.href = query
      ? `${FRONTEND_BASE_URL}/simulations?${query}`
      : `${FRONTEND_BASE_URL}/simulations`;
  }, [context]);

  const validate = () => {
    if (!form.projectName?.trim()) {
      notifyError("Please enter simulation name");
      return false;
    }
    const stage = FUNNEL_STAGES.find((s) => s.id === form.funnelStage);
    if (!stage) {
      notifyError("Please select a funnel stage");
      return false;
    }
    if (competitors.length === 0) {
      notifyError("Please add at least one competitor");
      return false;
    }
    return true;
  };

  const processPersonaFile = async (file) => {
    if (!file) return;
    if (personaParseLoading) return;
    if (!validate()) return;
    if (!file.name.toLowerCase().endsWith(".json")) {
      notifyError("Please upload a JSON file (.json)");
      return;
    }
    if (audienceMode === "upload") {
      if (context?.project_id == null || context?.project_id === "") {
        notifyError(
          "Select a project in session context before uploading a persona.",
        );
        return;
      }

      setPersonaParseLoading(true);
      try {
        const stage = FUNNEL_STAGES.find((s) => s.id === form.funnelStage);
        const res = await parseJsonFile({
          project_id: Number(context.project_id),
          persona: file,
          name: form.projectName?.trim() ?? "",
          description: form.simulationDescription?.trim() ?? "",
          language: form.language ?? "",
          llm_platforms: form.llmPlatforms,
          competitor_brands: competitors,
          funnel_stage: stage?.label ?? form.funnelStage ?? "",
        });
        if (res?.status >= 400) {
          notifyError(res?.data?.message ?? "Failed to process persona file");
          return;
        }

        setFieldsDisabled(true);
        const payload = res?.data ?? {};
        const inner =
          payload.response && typeof payload.response === "object"
            ? payload.response
            : {};
        const list = Array.isArray(inner.audiences) ? inner.audiences : [];
        setParsedAudiences(list);
        setParsedSimulationId(
          payload.simulation_id ?? inner.simulation_id ?? null,
        );
        setSelectedParsedAudienceId(getFirstAudienceId(list));
        resetGenerateUi();
        generateSnapshotRef.current = emptyGenerateSnapshot();
      } catch (e) {
        notifyError(
          e?.response?.data?.message ?? "Failed to process persona file",
        );
        setFieldsDisabled(false);
        return;
      } finally {
        setPersonaParseLoading(false);
      }
    } else {
      setParsedAudiences(null);
      setParsedSimulationId(null);
      setSelectedParsedAudienceId(null);
    }
    setSelectedAudienceId(null);
    setPersonaFile(file);
    setPersonaFileName(file.name);
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        JSON.parse(ev.target?.result);
      } catch {
        notifyError("Invalid JSON in persona file");
      }
    };
    reader.onerror = () => {
      notifyError("Could not read file");
    };
    reader.readAsText(file);
  };

  const toggleLibraryAudience = async (aud) => {
    if (selectedAudienceId === aud.id) {
      clearPersona();
      return;
    }
    resetGenerateUi();
    generateSnapshotRef.current = emptyGenerateSnapshot();
    const list = Array.isArray(aud?.audience_list) ? aud.audience_list : [];
    const fileNameFromUrl =
      typeof aud?.persona_url === "string"
        ? aud.persona_url.split("/").pop()
        : "";
    const nextFileName = aud?.persona_name || fileNameFromUrl || "persona.json";

    const parsedSel = getFirstAudienceId(list);
    librarySnapshotRef.current = {
      selectedAudienceId: aud.id,
      personaFileName: nextFileName,
      parsedAudiences: list,
      parsedSimulationId: aud?.id ?? null,
      selectedParsedAudienceId: parsedSel,
    };
    setParsedAudiences(list);
    setParsedSimulationId(aud?.id ?? null);
    setSelectedParsedAudienceId(parsedSel);
    setPersonaFile(null);
    setPersonaFileName(nextFileName);
    setSelectedAudienceId(aud.id);
  };

  const fetchPersonaFromLibrary = async () => {
    setPersonaLibraryLoading(true);
    try {
      const response = await getPersonaFromLibrary();
      if (response?.status < 400) {
        const rows = Array.isArray(response?.data) ? response.data : [];
        setAvailableAudiences(rows);
      } else {
        setAvailableAudiences([]);
      }
    } catch (error) {
      console.error("Failed to fetch persona from library", error);
      notifyError(
        error?.response?.data?.message ||
          "Failed to fetch persona from library",
      );
      setAvailableAudiences([]);
    } finally {
      setPersonaLibraryLoading(false);
    }
  };

  useEffect(() => {
    void fetchPersonaFromLibrary();
  }, []);

  useEffect(() => {
    if (personaLibraryLoading) return;
    if (buildPersonaFromData(data)) return;
    if (getPrefillAudiencePrompt(data)) return;
    if (data?.audienceGenerationFailed || data?.audience_generation_failed) return;

    const snapshot = prefillSnapshotKey(data);
    const simId = data?.simulationId ?? data?.simulation_id;
    if (simId == null || simId === "") return;
    if (!Array.isArray(availableAudiences) || availableAudiences.length === 0)
      return;

    const libRow = availableAudiences.find(
      (a) => String(a.id) === String(simId),
    );
    if (!libRow) return;

    if (libraryPreselectAppliedForKey.current === snapshot) return;
    libraryPreselectAppliedForKey.current = snapshot;

    const list = Array.isArray(libRow?.audience_list)
      ? libRow.audience_list
      : [];
    const selectedFromSource =
      data?.selectedAudience ?? data?.selected_audience;
    let parsedRowId = null;
    if (SKIP_AUDIENCE_SELECTION_STEP) {
      parsedRowId = getFirstAudienceId(list);
    } else if (
      selectedFromSource != null &&
      String(selectedFromSource).trim() !== ""
    ) {
      const want = String(selectedFromSource).trim();
      const match = list.find((row) => String(row.id ?? row.name) === want);
      if (match) parsedRowId = String(match.id ?? match.name);
    }
    if (parsedRowId == null && list.length > 0) {
      parsedRowId = getFirstAudienceId(list);
    }

    const fileNameFromUrl =
      typeof libRow?.persona_url === "string"
        ? libRow.persona_url.split("/").pop()
        : "";
    const nextFileName =
      libRow?.persona_name || fileNameFromUrl || "persona.json";

    librarySnapshotRef.current = {
      selectedAudienceId: libRow.id,
      personaFileName: nextFileName,
      parsedAudiences: list,
      parsedSimulationId: libRow.id ?? null,
      selectedParsedAudienceId: parsedRowId,
    };

    resetGenerateUi();
    generateSnapshotRef.current = emptyGenerateSnapshot();

    setAudienceMode("library");
    setSelectedAudienceId(libRow.id);
    setParsedAudiences(list);
    setParsedSimulationId(libRow.id ?? null);
    setSelectedParsedAudienceId(parsedRowId);
    setPersonaFile(null);
    setPersonaFileName(nextFileName);
  }, [personaLibraryLoading, availableAudiences, data, resetGenerateUi]);

  const updateForm = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const selectFunnel = (id) => {
    if (fieldsDisabled) return;
    setForm((p) => ({ ...p, funnelStage: id }));
  };
  const toggleLLM = (id) => {
    if (fieldsDisabled) return;
    setForm((p) => ({
      ...p,
      llmPlatforms: p.llmPlatforms.includes(id)
        ? p.llmPlatforms.filter((s) => s !== id)
        : [...p.llmPlatforms, id],
    }));
  };

  const handlePersonaFileChange = (e) => {
    const input = e?.target;
    const file = input?.files?.[0];
    if (!file) {
      if (input) input.value = "";
      return;
    }
    void processPersonaFile(file).finally(() => {
      setTimeout(() => {
        if (input) input.value = "";
      }, 0);
    });
  };

  const handlePersonaDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (personaParseLoading) return;
    const file = e.dataTransfer?.files?.[0];
    if (file) void processPersonaFile(file);
  };

  const handleAudienceSelection = async (val) => {
    setSelectedParsedAudienceId(val);
    librarySnapshotRef.current = {
      ...librarySnapshotRef.current,
      selectedParsedAudienceId: val,
    };
  };

  const handleSave = async (
    continueToPersona = false,
    is_new_simulation = false,
    simulation_id_override = null,
    selected_audience_override = undefined,
  ) => {
    if (submitLoading) return;

    const effectiveSimId =
      simulation_id_override !== null && simulation_id_override !== undefined
        ? simulation_id_override
        : parsedSimulationId;
    const effectiveAudience = resolveSelectedAudienceId(
      parsedAudiences,
      selected_audience_override !== undefined &&
        selected_audience_override !== null &&
        String(selected_audience_override).trim() !== ""
        ? selected_audience_override
        : selectedParsedAudienceId,
    );

    if (effectiveSimId == null || effectiveSimId === "") {
      notifyError(
        "Select a persona and wait for processing to complete before continuing.",
      );
      return;
    }
    if (!SKIP_AUDIENCE_SELECTION_STEP && !effectiveAudience) {
      notifyError(
        "Select a persona and wait for audiences to load before saving.",
      );
      return;
    }
    if (SKIP_AUDIENCE_SELECTION_STEP && !canSavePersona) {
      notifyError("Persona is not ready yet. Wait for processing to complete.");
      return;
    }

    setSubmitLoading(true);

    try {
      const res = await saveSimulation(
        buildGenerateQueriesPayload(effectiveSimId, {
          isNewSimulation: is_new_simulation,
          selectedAudience: effectiveAudience,
        }),
      );
      if (res?.status < 400) {
        dispatch(toggleRefresh());
        const nextId =
          res?.data?.project_id ?? res?.data?.simulation_id ?? effectiveSimId;
        notifySuccess(
          res?.data?.message ??
            (SKIP_AUDIENCE_SELECTION_STEP
              ? "Query generation started successfully"
              : "Simulation saved successfully"),
        );
        if (SKIP_AUDIENCE_SELECTION_STEP) {
          onQueriesGenerationStarted({
            id: effectiveSimId,
            name: form.projectName.trim(),
          });
        } else if (continueToPersona) onSaveAndGo?.(nextId);
        else onBack?.();
      } else {
        notifyError(res?.data?.message ?? "Failed to save simulation");
      }
    } catch (err) {
      notifyError(err?.response?.data?.message ?? "Failed to save simulation");
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleSelectFromLibrary = async (
    audienceId = selectedParsedAudienceId,
    simulationId = parsedSimulationId,
  ) => {
    if (!validate()) return;

    if (
      audienceMode === "upload" ||
      audienceMode === "generate" ||
      gwiGenerated
    ) {
      await handleSave(false, false, simulationId, audienceId);
      return;
    }

    const payload = {
      name: form.projectName.trim(),
      description: form.simulationDescription?.trim() ?? "",
      simulation_id: simulationId,
      project_id: Number(context.project_id),
      funnel_stage:
        FUNNEL_STAGES.find((s) => s.id === form.funnelStage)?.label ??
        form.funnelStage,
      competitor_brands: competitors,
      language: form.language,
      llm_platform: Array.isArray(form.llmPlatforms)
        ? form.llmPlatforms[0]
        : form.llmPlatforms,
      llm_platforms: Array.isArray(form.llmPlatforms)
        ? form.llmPlatforms
        : [form.llmPlatforms],
      selected_audience: String(audienceId),
    };
    if (SKIP_AUDIENCE_SELECTION_STEP) {
      delete payload.selected_audience;
    }

    setPersonaParseLoading(true);
    try {
      const res = await reRunSimulation(payload);
      if (res?.status < 400) {
        const newSimId = res?.data?.simulation_id ?? null;
        setParsedSimulationId(newSimId);
        setSelectedParsedAudienceId(audienceId);
        await handleSave(false, true, newSimId, audienceId);
      }
    } catch (error) {
      notifyError(
        error?.response?.data?.message ?? "Failed to save simulation",
      );
    } finally {
      setPersonaParseLoading(false);
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <style>
        {`
          @keyframes duplicateSimPersonaSpin {
            to { transform: rotate(360deg); }
          }
        `}
      </style>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <button
            type="button"
            onClick={onBack}
            style={{
              padding: "8px 14px",
              borderRadius: 8,
              border: "1px solid rgba(0,0,0,0.12)",
              background: "transparent",
              color: "#6b6b6b",
              fontSize: 13,
              cursor: "pointer",
              fontFamily: font,
              display: "flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            ← Back to Simulations
          </button>
          <div>
            <h1
              style={{
                fontSize: 24,
                fontWeight: 700,
                color: "#363636",
                margin: 0,
                fontFamily: font,
              }}
            >
              Duplicate Simulation
            </h1>
            <p
              style={{
                fontSize: 13,
                color: "#999999",
                margin: "4px 0 0",
                fontFamily: font,
              }}
            >
              Form is prefilled from the source run. Edit anything before
              saving. Fields marked with{" "}
              <span style={{ color: "#ef4444" }}>*</span> are required
            </p>
          </div>
        </div>
      </div>

      <div style={{ display: "flex", gap: 28, alignItems: "flex-start" }}>
        <div style={{ flex: "1 1 620px" }}>
          <Card>
            <SectionDivider label="Simulation Details" />
            <FormInput
              label="Simulation Name"
              placeholder="e.g., E.ON Sustainable Energy (Copy)"
              helpText="Use a descriptive name: Client + Topic works well."
              icon="📋"
              required
              value={form.projectName}
              onChange={(v) => updateForm("projectName", v)}
              disabled={fieldsDisabled}
            />
            <FormInput
              label="Simulation description"
              placeholder="What this simulation covers, goals, or context for your team."
              helpText="Optional — improves AI results when filled in. Adding context about audience intent, competitive focus, or specific angles produces more relevant results."
              icon="📝"
              required={false}
              multiline
              rows={3}
              value={form.simulationDescription}
              onChange={(v) => updateForm("simulationDescription", v)}
              disabled={fieldsDisabled}
            />
            <FormInput
              label="Project"
              placeholder="—"
              helpText="Read-only — taken from session context when you select a project."
              icon="📁"
              required={false}
              readOnly
              disabled={fieldsDisabled}
              value={context?.project_name ?? ""}
            />

            <div
              style={{
                marginBottom: 20,
                cursor: fieldsDisabled ? "not-allowed" : undefined,
              }}
            >
              <label
                style={{
                  display: "block",
                  fontSize: 13,
                  color: "#6b6b6b",
                  marginBottom: 6,
                  fontFamily: font,
                }}
              >
                Language <span style={{ color: "#ef4444" }}>*</span>
              </label>
              <SearchableDrp
                options={LANGUAGES}
                value={form.language}
                onChange={(v) => updateForm("language", v)}
                placeholder="Select language..."
                icon="🌐"
                disabled={fieldsDisabled}
              />
            </div>

            <SectionDivider label="Competitor Tracking" />
            <TagInput
              label="Competitor Brands"
              placeholder="Type a competitor name and press Enter"
              helpText="Used for share of voice and competitive gap analysis. Press Enter or comma to add."
              required
              tags={competitors}
              onAdd={(t) => setCompetitors((p) => [...p, t])}
              onRemove={(i) =>
                setCompetitors((p) => p.filter((_, idx) => idx !== i))
              }
              disabled={fieldsDisabled}
            />

            <SectionDivider label="Simulation Settings" />
            <div
              style={{
                marginBottom: 24,
                cursor: fieldsDisabled ? "not-allowed" : undefined,
              }}
            >
              <label
                style={{
                  display: "block",
                  fontSize: 13,
                  color: "#6b6b6b",
                  marginBottom: 10,
                  fontFamily: font,
                }}
              >
                Funnel Stage <span style={{ color: "#ef4444" }}>*</span>
              </label>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 10,
                }}
              >
                {FUNNEL_STAGES.map((stage) => {
                  const sel = form.funnelStage === stage.id;
                  return (
                    <div
                      key={stage.id}
                      onClick={() => selectFunnel(stage.id)}
                      style={{
                        padding: "14px 16px",
                        borderRadius: 10,
                        cursor: fieldsDisabled ? "not-allowed" : "pointer",
                        opacity: fieldsDisabled ? 0.85 : 1,
                        background: sel
                          ? "rgba(239,68,68,0.08)"
                          : "rgba(0,0,0,0.03)",
                        border: sel
                          ? "1px solid rgba(239,68,68,0.3)"
                          : "1px solid rgba(0,0,0,0.06)",
                        transition: "all 0.15s",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                          marginBottom: 4,
                        }}
                      >
                        <span style={{ fontSize: 16 }}>{stage.icon}</span>
                        <span
                          style={{
                            fontSize: 13,
                            fontWeight: 600,
                            color: sel ? "#363636" : "#64748b",
                            fontFamily: font,
                          }}
                        >
                          {stage.label}
                        </span>
                        {sel && (
                          <span
                            style={{
                              marginLeft: "auto",
                              width: 18,
                              height: 18,
                              borderRadius: "50%",
                              background: "#ef4444",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: 11,
                              color: "#fff",
                            }}
                          >
                            ✓
                          </span>
                        )}
                      </div>
                      <p
                        style={{
                          fontSize: 11,
                          color: "#999999",
                          margin: "0 0 4px",
                          lineHeight: 1.4,
                          fontFamily: font,
                        }}
                      >
                        {stage.desc}
                      </p>
                      <p
                        style={{
                          fontSize: 10,
                          color: "#6b7280",
                          margin: 0,
                          fontStyle: "italic",
                          lineHeight: 1.3,
                          fontFamily: font,
                        }}
                      >
                        {stage.example}
                      </p>
                    </div>
                  );
                })}
              </div>
              <p
                style={{
                  fontSize: 11,
                  color: "#999999",
                  marginTop: 8,
                  lineHeight: 1.4,
                  fontFamily: font,
                }}
              >
                Select a single funnel stage. Each stage generates different
                query types and evaluates different KPIs.
              </p>
            </div>

            <div
              style={{
                marginBottom: 28,
                cursor: fieldsDisabled ? "not-allowed" : undefined,
              }}
            >
              <label
                style={{
                  display: "block",
                  fontSize: 13,
                  color: "#6b6b6b",
                  marginBottom: 10,
                  fontFamily: font,
                }}
              >
                LLM Platforms <span style={{ color: "#ef4444" }}>*</span>
              </label>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr 1fr 1fr",
                  gap: 10,
                }}
              >
                {LLM_PLATFORMS.map((p) => {
                  const sel = form.llmPlatforms.includes(p.id);
                  return (
                    <div
                      key={p.id}
                      onClick={() => toggleLLM(p.id)}
                      style={{
                        padding: "14px 12px",
                        borderRadius: 10,
                        cursor: fieldsDisabled ? "not-allowed" : "pointer",
                        background: sel
                          ? "rgba(239,68,68,0.08)"
                          : "rgba(0,0,0,0.03)",
                        border: sel
                          ? "1px solid rgba(239,68,68,0.3)"
                          : "1px solid rgba(0,0,0,0.06)",
                        opacity: fieldsDisabled ? 0.85 : 1,
                        textAlign: "center",
                        position: "relative",
                        transition: "all 0.15s",
                      }}
                    >
                      <div
                        style={{
                          width: 10,
                          height: 10,
                          borderRadius: "50%",
                          background: p.color,
                          margin: "0 auto 8px",
                          opacity: sel ? 1 : 0.5,
                        }}
                      />
                      <div
                        style={{
                          fontSize: 13,
                          fontWeight: 600,
                          color: sel ? "#363636" : "#64748b",
                          fontFamily: font,
                        }}
                      >
                        {p.label}
                      </div>
                      {p.provider && (
                        <div
                          style={{
                            fontSize: 11,
                            color: "#999999",
                            marginTop: 2,
                            fontFamily: font,
                          }}
                        >
                          {p.provider}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              <p
                style={{
                  fontSize: 11,
                  color: "#999999",
                  marginTop: 6,
                  lineHeight: 1.4,
                  fontFamily: font,
                }}
              >
                Select one or more LLM platforms for this simulation.
              </p>
            </div>

            <SectionDivider label="Audience" />
            <div style={{ marginBottom: 20 }}>
              <label
                style={{
                  display: "block",
                  fontSize: 13,
                  color: "#6b6b6b",
                  marginBottom: 10,
                  fontFamily: font,
                }}
              >
                Persona (Persona JSON){" "}
                <span style={{ color: "#ef4444" }}>*</span>
              </label>
              <input
                ref={personaInputRef}
                id="duplicate-persona-file-input"
                type="file"
                accept=".json,application/json"
                onChange={handlePersonaFileChange}
                style={{
                  position: "absolute",
                  width: 1,
                  height: 1,
                  opacity: 0,
                  overflow: "hidden",
                  clip: "rect(0,0,0,0)",
                }}
              />
              <div
                style={{
                  display: "flex",
                  gap: 0,
                  marginBottom: 14,
                  borderRadius: 8,
                  overflow: "hidden",
                  border: "1px solid rgba(0,0,0,0.08)",
                }}
              >
                {[
                  { id: "generate", label: "Generate" },
                  { id: "library", label: "Select from Library" },
                  { id: "upload", label: "Upload New" },
                ].map((mode) => {
                  const active = audienceMode === mode.id;
                  return (
                    <div
                      key={mode.id}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          handleAudienceMode(mode.id);
                        }
                      }}
                      onClick={() => handleAudienceMode(mode.id)}
                      style={{
                        flex: 1,
                        padding: "10px 16px",
                        textAlign: "center",
                        cursor: "pointer",
                        opacity: 1,
                        background: active
                          ? "rgba(239,68,68,0.08)"
                          : "rgba(0,0,0,0.03)",
                        color: active ? "#ef4444" : "#6b7a90",
                        fontSize: 13,
                        fontWeight: 600,
                        fontFamily: font,
                        borderBottom: active
                          ? "2px solid #ef4444"
                          : "2px solid transparent",
                        transition: "all 0.15s",
                      }}
                    >
                      {mode.label}
                    </div>
                  );
                })}
              </div>

              {audienceMode === "generate" && (
                <div style={{ position: "relative" }}>
                  {!gwiGenerated ? (
                    <div>
                      <textarea
                        value={gwiPrompt}
                        onChange={(e) => setGwiPrompt(e.target.value)}
                        placeholder="e.g. Electric vehicle buyers in Germany aged 30-55"
                        rows={3}
                        disabled={fieldsDisabled}
                        style={{
                          width: "100%",
                          padding: "12px 14px",
                          borderRadius: 8,
                          border: "1px solid rgba(0,0,0,0.1)",
                          background: "rgba(0,0,0,0.03)",
                          color: "#363636",
                          fontSize: 13,
                          fontFamily: font,
                          resize: "vertical",
                          outline: "none",
                          boxSizing: "border-box",
                          lineHeight: 1.5,
                        }}
                      />
                      <div
                        style={{
                          display: "flex",
                          gap: 8,
                          marginTop: 10,
                          alignItems: "center",
                        }}
                      >
                        <button
                          type="button"
                          disabled={
                            !gwiPrompt.trim() ||
                            !selectedBrandName ||
                            gwiGenerating ||
                            fieldsDisabled ||
                            !context?.project_id
                          }
                          onClick={handleConfirmGeneratePersona}
                          style={{
                            padding: "9px 18px",
                            borderRadius: 8,
                            border: "none",
                            background:
                              !gwiPrompt.trim() ||
                              !selectedBrandName ||
                              gwiGenerating ||
                              fieldsDisabled ||
                              !context?.project_id
                                ? "rgba(239,68,68,0.2)"
                                : "#ef4444",
                            color:
                              !gwiPrompt.trim() ||
                              !selectedBrandName ||
                              gwiGenerating ||
                              fieldsDisabled ||
                              !context?.project_id
                                ? "rgba(255,255,255,0.85)"
                                : "#fff",
                            fontSize: 13,
                            fontWeight: 600,
                            fontFamily: font,
                            cursor:
                              !gwiPrompt.trim() ||
                              !selectedBrandName ||
                              gwiGenerating ||
                              fieldsDisabled ||
                              !context?.project_id
                                ? "not-allowed"
                                : "pointer",
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                          }}
                        >
                          {gwiGenerating ? (
                            <>
                              <span
                                style={{
                                  width: 14,
                                  height: 14,
                                  borderRadius: "50%",
                                  border: "2px solid rgba(255,255,255,0.35)",
                                  borderTopColor: "#fff",
                                  animation:
                                    "duplicateSimPersonaSpin 0.75s linear infinite",
                                  display: "inline-block",
                                }}
                              />
                              Generating...
                            </>
                          ) : (
                            <>✨ Generate Persona</>
                          )}
                        </button>
                      </div>
                      {!selectedBrandName && (
                        <p
                          style={{
                            fontSize: 11,
                            color: "#f59e0b",
                            marginTop: 8,
                            lineHeight: 1.4,
                            fontFamily: font,
                          }}
                        >
                          Select session brand/context first to generate a
                          persona.
                        </p>
                      )}
                      {!context?.project_id && selectedBrandName ? (
                        <p
                          style={{
                            fontSize: 11,
                            color: "#f59e0b",
                            marginTop: 8,
                            lineHeight: 1.4,
                            fontFamily: font,
                          }}
                        >
                          Select a project in session context before generating.
                        </p>
                      ) : null}
                      <p
                        style={{
                          fontSize: 11,
                          color: "#999999",
                          marginTop: 8,
                          lineHeight: 1.4,
                          fontFamily: font,
                        }}
                      >
                        Describe your audience; after you confirm, we generate
                        persona segments from GWI-style data for you to select
                        and save.
                      </p>
                    </div>
                  ) : (
                    <div>
                      <div
                        style={{
                          width: "100%",
                          maxWidth: "100%",
                          boxSizing: "border-box",
                          background: "rgba(34,197,94,0.08)",
                          border: "1px solid rgba(34,197,94,0.25)",
                          borderRadius: 10,
                          padding: "14px 16px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 12,
                          marginBottom: 12,
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 12,
                            minWidth: 0,
                          }}
                        >
                          <span style={{ fontSize: 20, flexShrink: 0 }}>✓</span>
                          <div
                            style={{
                              fontSize: 13,
                              fontWeight: 600,
                              color: "#363636",
                              fontFamily: font,
                            }}
                          >
                            Persona generated successfully
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={clearGeneratePersona}
                          style={{
                            border: "none",
                            background: "transparent",
                            color: "#999999",
                            cursor: "pointer",
                            fontSize: 18,
                            lineHeight: 1,
                            flexShrink: 0,
                            padding: 4,
                          }}
                          aria-label="Remove generated persona"
                        >
                          ×
                        </button>
                      </div>
                      {!SKIP_AUDIENCE_SELECTION_STEP ? (
                        <ParsedAudiencesPanel
                          audiences={parsedAudiences}
                          selectedAudienceId={selectedParsedAudienceId}
                          onSelectAudience={setSelectedParsedAudienceId}
                        />
                      ) : (
                        canSavePersona && (
                          <AudienceContinueButton
                            onClick={() => handleSelectFromLibrary()}
                            loading={submitLoading}
                            disabled={
                              submitLoading ||
                              personaParseLoading ||
                              gwiGenerating
                            }
                          />
                        )
                      )}
                    </div>
                  )}
                  {gwiGenerating && (
                    <div
                      role="status"
                      aria-live="polite"
                      aria-busy="true"
                      style={{
                        position: "absolute",
                        inset: 0,
                        borderRadius: 8,
                        background: "rgba(255,255,255,0.92)",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 12,
                        zIndex: 2,
                        minHeight: 160,
                        padding: "16px 12px",
                        boxSizing: "border-box",
                      }}
                    >
                      <div
                        style={{
                          width: 32,
                          height: 32,
                          borderRadius: "50%",
                          border: "3px solid rgba(0,0,0,0.1)",
                          borderTopColor: "#ef4444",
                          animation:
                            "duplicateSimPersonaSpin 0.75s linear infinite",
                          flexShrink: 0,
                        }}
                      />
                      <div
                        style={{
                          fontSize: 13,
                          color: "#64748b",
                          fontFamily: font,
                          textAlign: "center",
                          lineHeight: 1.55,
                          maxWidth: "100%",
                          padding: "0 8px",
                          boxSizing: "border-box",
                        }}
                      >
                        {GENERATING_AUDIENCE_OVERLAY_MESSAGE}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {audienceMode === "library" && (
                <div>
                  {personaLibraryLoading ? (
                    <div
                      style={{
                        padding: "20px 14px",
                        textAlign: "center",
                        background: "rgba(0,0,0,0.03)",
                        borderRadius: 8,
                        border: "1px dashed rgba(0,0,0,0.08)",
                      }}
                    >
                      <div
                        style={{
                          fontSize: 13,
                          color: "#999999",
                          fontFamily: font,
                        }}
                      >
                        Loading persona library...
                      </div>
                    </div>
                  ) : !context?.project_id ? (
                    <div
                      style={{
                        padding: "20px 14px",
                        textAlign: "center",
                        background: "rgba(0,0,0,0.03)",
                        borderRadius: 8,
                        border: "1px dashed rgba(0,0,0,0.08)",
                      }}
                    >
                      <div
                        style={{
                          fontSize: 13,
                          color: "#999999",
                          fontFamily: font,
                        }}
                      >
                        Select a project first to see available audiences.
                      </div>
                    </div>
                  ) : availableAudiences.length === 0 ? (
                    <div
                      style={{
                        padding: "20px 14px",
                        textAlign: "center",
                        background: "rgba(0,0,0,0.03)",
                        borderRadius: 8,
                        border: "1px dashed rgba(0,0,0,0.08)",
                      }}
                    >
                      <div
                        style={{
                          fontSize: 13,
                          color: "#999999",
                          fontFamily: font,
                        }}
                      >
                        No personas in the library.
                      </div>
                      <div
                        style={{
                          fontSize: 12,
                          color: "#ef4444",
                          marginTop: 8,
                          cursor: "pointer",
                          fontFamily: font,
                        }}
                        onClick={() => handleAudienceMode("upload")}
                        role="presentation"
                      >
                        Upload a new audience →
                      </div>
                    </div>
                  ) : (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 8,
                        maxHeight: 270,
                        overflowY: "auto",
                        overflowX: "hidden",
                        paddingRight: 6,
                        marginRight: -2,
                      }}
                    >
                      {availableAudiences.map((aud) => {
                        const sel = selectedAudienceId === aud.id;
                        return (
                          <div
                            key={aud.id}
                            onClick={() => void toggleLibraryAudience(aud)}
                            style={{
                              padding: "14px 16px",
                              borderRadius: 10,
                              cursor: "pointer",
                              background: sel
                                ? "rgba(239,68,68,0.08)"
                                : "rgba(0,0,0,0.03)",
                              border: sel
                                ? "1px solid rgba(239,68,68,0.3)"
                                : "1px solid rgba(0,0,0,0.06)",
                              transition: "all 0.15s",
                              display: "flex",
                              alignItems: "center",
                              gap: 12,
                            }}
                          >
                            <div
                              style={{
                                width: 18,
                                height: 18,
                                borderRadius: "50%",
                                border: sel
                                  ? "2px solid #ef4444"
                                  : "2px solid rgba(0,0,0,0.18)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                flexShrink: 0,
                              }}
                            >
                              {sel && (
                                <div
                                  style={{
                                    width: 10,
                                    height: 10,
                                    borderRadius: "50%",
                                    background: "#ef4444",
                                  }}
                                />
                              )}
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div
                                style={{
                                  fontSize: 13,
                                  fontWeight: 600,
                                  color: sel ? "#363636" : "#64748b",
                                  fontFamily: font,
                                }}
                              >
                                {aud?.persona_name ||
                                  `Persona ${aud?.id ?? ""}`}
                              </div>
                              <div
                                style={{
                                  fontSize: 11,
                                  color: "#999999",
                                  fontFamily: font,
                                  marginTop: 2,
                                }}
                              >
                                {Array.isArray(aud?.audience_list)
                                  ? ` · ${aud.audience_list.length} audiences`
                                  : " · 0 audiences"}
                              </div>
                            </div>
                            {sel && (
                              <span
                                style={{
                                  color: "#ef4444",
                                  fontSize: 14,
                                  flexShrink: 0,
                                }}
                              >
                                ✓
                              </span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                  <p
                    style={{
                      fontSize: 11,
                      color: "#999999",
                      marginTop: 8,
                      lineHeight: 1.4,
                      fontFamily: font,
                    }}
                  >
                    Persona library from API. Select a persona to load its
                    audiences.
                    {selectedBrandName
                      ? ` Brand context: ${selectedBrandName}.`
                      : ""}
                  </p>
                </div>
              )}

              {audienceMode === "upload" && (
                <div>
                  <div style={{ position: "relative", borderRadius: 8 }}>
                    {!personaFile ? (
                      <div
                        onClick={() => {
                          if (personaParseLoading) return;
                          personaInputRef.current?.click();
                        }}
                        onDragOver={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                        }}
                        onDrop={handlePersonaDrop}
                        style={{
                          background: "rgba(0,0,0,0.04)",
                          border: "1px dashed rgba(0,0,0,0.1)",
                          borderRadius: 8,
                          padding: "20px 14px",
                          cursor: personaParseLoading ? "wait" : "pointer",
                          textAlign: "center",
                          opacity: personaParseLoading ? 0.55 : 1,
                        }}
                      >
                        <div
                          style={{
                            marginBottom: 8,
                            opacity: 0.35,
                            display: "flex",
                            justifyContent: "center",
                            fontSize: 28,
                          }}
                        >
                          📁
                        </div>
                        <div
                          style={{
                            fontSize: 13,
                            color: "#64748b",
                            fontFamily: font,
                          }}
                        >
                          Drop your persona file here or{" "}
                          <span style={{ color: "#ef4444" }}>browse</span>
                        </div>
                        <div
                          style={{
                            fontSize: 11,
                            color: "#999999",
                            marginTop: 6,
                            fontFamily: font,
                          }}
                        >
                          The{" "}
                          <span style={{ color: "#363636", fontWeight: 600 }}>
                            Simulation name, description, Language, Funnel Stage
                            and other fields
                          </span>{" "}
                          will be used to generate the persona. So once audience
                          is generated you cannot change all of these settings.
                        </div>
                      </div>
                    ) : (
                      <div
                        style={{
                          background: "rgba(0,0,0,0.04)",
                          border: "1px solid rgba(0,0,0,0.08)",
                          borderRadius: 8,
                          padding: "12px 14px",
                          display: "flex",
                          alignItems: "center",
                          gap: 12,
                          opacity: personaParseLoading ? 0.55 : 1,
                        }}
                      >
                        <span
                          style={{
                            display: "flex",
                            alignItems: "center",
                            fontSize: 22,
                          }}
                        >
                          📄
                        </span>
                        <div style={{ flex: 1 }}>
                          <div
                            style={{
                              fontSize: 13,
                              color: "#363636",
                              fontWeight: 500,
                              fontFamily: font,
                            }}
                          >
                            {personaFileName}
                          </div>
                          <div
                            style={{
                              fontSize: 11,
                              color: "#999999",
                              fontFamily: font,
                            }}
                          >
                            {(personaFile.size / 1024).toFixed(1)} KB · GWI
                            Persona Profile
                          </div>
                        </div>
                        <span
                          onClick={() => {
                            if (!personaParseLoading) clearPersona();
                          }}
                          style={{
                            color: "#999999",
                            cursor: personaParseLoading
                              ? "not-allowed"
                              : "pointer",
                            fontSize: 18,
                            lineHeight: 1,
                          }}
                          role="presentation"
                        >
                          ×
                        </span>
                      </div>
                    )}
                    {personaParseLoading && (
                      <div
                        role="status"
                        aria-live="polite"
                        aria-busy="true"
                        style={{
                          position: "absolute",
                          inset: 0,
                          borderRadius: 8,
                          background: "rgba(255,255,255,0.92)",
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 12,
                          zIndex: 2,
                        }}
                      >
                        <div
                          style={{
                            width: 32,
                            height: 32,
                            borderRadius: "50%",
                            border: "3px solid rgba(0,0,0,0.1)",
                            borderTopColor: "#ef4444",
                            animation:
                              "duplicateSimPersonaSpin 0.75s linear infinite",
                          }}
                        />
                        <span
                          style={{
                            fontSize: 13,
                            color: "#64748b",
                            fontFamily: font,
                          }}
                        >
                          Processing persona…
                        </span>
                      </div>
                    )}
                  </div>
                  <p
                    style={{
                      fontSize: 11,
                      color: "#999999",
                      marginTop: 4,
                      lineHeight: 1.4,
                      fontFamily: font,
                    }}
                  >
                    Persona name or file may be prefilled from the source
                    simulation. Once selecting an audience from Upload New tab,
                    the fields will be disabled.
                  </p>
                  {audienceMode === "upload" &&
                    (!gwiGenerated || personaFile) &&
                    (!SKIP_AUDIENCE_SELECTION_STEP ? (
                      <ParsedAudiencesPanel
                        audiences={parsedAudiences}
                        selectedAudienceId={selectedParsedAudienceId}
                        onSelectAudience={setSelectedParsedAudienceId}
                      />
                    ) : (
                      canSavePersona && (
                        <AudienceContinueButton
                          onClick={() => handleSelectFromLibrary()}
                          loading={submitLoading}
                          disabled={
                            submitLoading ||
                            personaParseLoading ||
                            gwiGenerating
                          }
                        />
                      )
                    ))}
                </div>
              )}
              {audienceMode === "library" &&
                selectedAudienceId != null &&
                (!SKIP_AUDIENCE_SELECTION_STEP ? (
                  <ParsedAudiencesPanel
                    audiences={parsedAudiences}
                    selectedAudienceId={selectedParsedAudienceId}
                    onSelectAudience={handleAudienceSelection}
                  />
                ) : (
                  canSavePersona && (
                    <AudienceContinueButton
                      onClick={() => handleSelectFromLibrary()}
                      loading={submitLoading}
                      disabled={
                        submitLoading ||
                        personaParseLoading ||
                        gwiGenerating
                      }
                    />
                  )
                ))}

              <GeneratePersonaConfirmModal
                open={generateConfirmOpen}
                onClose={handleGeneratePersonaRedirect}
                onConfirm={handleGeneratePersonaRedirect}
                disabled={gwiGenerating}
                title="Persona generation started"
                message={generatePopupMessage}
              />
            </div>

            {!SKIP_AUDIENCE_SELECTION_STEP && (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <button
                type="button"
                onClick={() => handleSelectFromLibrary()}
                disabled={
                  submitLoading ||
                  personaParseLoading ||
                  gwiGenerating ||
                  !canSavePersona
                }
                style={{
                  width: "100%",
                  padding: "14px 0",
                  borderRadius: 10,
                  border: "none",
                  background: "linear-gradient(135deg, #ef4444, #dc2626)",
                  color: "#fff",
                  fontSize: 15,
                  fontWeight: 600,
                  cursor:
                    submitLoading ||
                    personaParseLoading ||
                    gwiGenerating ||
                    !canSavePersona
                      ? "not-allowed"
                      : "pointer",
                  fontFamily: font,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 8,
                  boxShadow: "0 4px 20px rgba(239,68,68,0.25)",
                  opacity:
                    submitLoading ||
                    personaParseLoading ||
                    gwiGenerating ||
                    !canSavePersona
                      ? 0.7
                      : 1,
                }}
              >
                {submitLoading
                  ? "Saving..."
                  : personaParseLoading || gwiGenerating
                    ? "Analyzing audience..."
                    : "💾 Save Simulation"}
              </button>
            </div>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}

export default DuplicateSimulation;
